import React, { useState, useEffect } from 'react';
import { Table, Button, Space, Modal, Form, Input, Select, Switch, message, Spin, Card } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import { cmsAPI } from '../services/api';

const { TextArea } = Input;

const ContentPagesTab: React.FC = () => {
  const [sections, setSections] = useState<any[]>([]);
  const [pages, setPages] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [sectionModal, setSectionModal] = useState(false);
  const [pageModal, setPageModal] = useState(false);
  const [editingSection, setEditingSection] = useState<any>(null);
  const [editingPage, setEditingPage] = useState<any>(null);
  const [sectionForm] = Form.useForm();
  const [pageForm] = Form.useForm();
  const [pageContent, setPageContent] = useState('');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [sectionsData, pagesData] = await Promise.all([
        cmsAPI.adminGetSections(),
        cmsAPI.adminGetPages(),
      ]);
      setSections(sectionsData);
      setPages(pagesData);
    } catch (error: any) {
      message.error(`加载失败: ${error?.response?.data?.detail || error.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Section CRUD
  const handleCreateSection = () => {
    setEditingSection(null);
    sectionForm.resetFields();
    setSectionModal(true);
  };

  const handleEditSection = (record: any) => {
    setEditingSection(record);
    sectionForm.setFieldsValue(record);
    setSectionModal(true);
  };

  const handleSaveSection = async () => {
    try {
      const values = await sectionForm.validateFields();
      if (editingSection) {
        await cmsAPI.adminUpdateSection(editingSection.id, values);
        message.success('更新成功');
      } else {
        await cmsAPI.adminCreateSection(values);
        message.success('创建成功');
      }
      setSectionModal(false);
      loadData();
    } catch (error: any) {
      message.error(`保存失败: ${error?.response?.data?.detail || error.message}`);
    }
  };

  const handleDeleteSection = (record: any) => {
    Modal.confirm({
      title: '确认删除',
      content: `确定要删除板块"${record.name}"吗？这将同时删除该板块下的所有页面。`,
      onOk: async () => {
        try {
          await cmsAPI.adminDeleteSection(record.id);
          message.success('删除成功');
          loadData();
        } catch (error: any) {
          message.error(`删除失败: ${error?.response?.data?.detail || error.message}`);
        }
      },
    });
  };

  // Page CRUD
  const handleCreatePage = () => {
    setEditingPage(null);
    pageForm.resetFields();
    setPageContent('');
    setPageModal(true);
  };

  const handleEditPage = (record: any) => {
    setEditingPage(record);
    pageForm.setFieldsValue({
      ...record,
      content: undefined, // Don't set content in form, use state
    });
    setPageContent(record.content || '');
    setPageModal(true);
  };

  const handleSavePage = async () => {
    try {
      const values = await pageForm.validateFields();
      const data = { ...values, content: pageContent };
      if (editingPage) {
        await cmsAPI.adminUpdatePage(editingPage.id, data);
        message.success('更新成功');
      } else {
        await cmsAPI.adminCreatePage(data);
        message.success('创建成功');
      }
      setPageModal(false);
      loadData();
    } catch (error: any) {
      message.error(`保存失败: ${error?.response?.data?.detail || error.message}`);
    }
  };

  const handleDeletePage = (record: any) => {
    Modal.confirm({
      title: '确认删除',
      content: `确定要删除页面"${record.title}"吗？`,
      onOk: async () => {
        try {
          await cmsAPI.adminDeletePage(record.id);
          message.success('删除成功');
          loadData();
        } catch (error: any) {
          message.error(`删除失败: ${error?.response?.data?.detail || error.message}`);
        }
      },
    });
  };

  const sectionColumns = [
    { title: '板块名称', dataIndex: 'name', key: 'name' },
    { title: 'Slug', dataIndex: 'slug', key: 'slug' },
    { title: '描述', dataIndex: 'description', key: 'description', ellipsis: true },
    { title: '排序', dataIndex: 'sort_order', key: 'sort_order', width: 80 },
    { title: '页面数', dataIndex: 'page_count', key: 'page_count', width: 80 },
    {
      title: '状态',
      dataIndex: 'is_active',
      key: 'is_active',
      width: 80,
      render: (val: boolean) => (val ? '启用' : '禁用'),
    },
    {
      title: '操作',
      key: 'actions',
      width: 150,
      render: (_: any, record: any) => (
        <Space>
          <Button size="small" icon={<EditOutlined />} onClick={() => handleEditSection(record)}>
            编辑
          </Button>
          <Button size="small" danger icon={<DeleteOutlined />} onClick={() => handleDeleteSection(record)}>
            删除
          </Button>
        </Space>
      ),
    },
  ];

  const pageColumns = [
    { title: '标题', dataIndex: 'title', key: 'title' },
    { title: 'Slug', dataIndex: 'slug', key: 'slug' },
    { title: '所属板块', dataIndex: 'section_name', key: 'section_name' },
    {
      title: '类型',
      dataIndex: 'is_external',
      key: 'is_external',
      width: 80,
      render: (val: boolean) => (val ? '外链' : '内容'),
    },
    { title: '排序', dataIndex: 'sort_order', key: 'sort_order', width: 80 },
    {
      title: '状态',
      dataIndex: 'is_active',
      key: 'is_active',
      width: 80,
      render: (val: boolean) => (val ? '启用' : '禁用'),
    },
    {
      title: '操作',
      key: 'actions',
      width: 150,
      render: (_: any, record: any) => (
        <Space>
          <Button size="small" icon={<EditOutlined />} onClick={() => handleEditPage(record)}>
            编辑
          </Button>
          <Button size="small" danger icon={<DeleteOutlined />} onClick={() => handleDeletePage(record)}>
            删除
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <Spin spinning={loading}>
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        {/* Sections */}
        <Card
          title="内容板块"
          extra={
            <Button type="primary" icon={<PlusOutlined />} onClick={handleCreateSection}>
              新建板块
            </Button>
          }
        >
          <Table
            dataSource={sections}
            columns={sectionColumns}
            rowKey="id"
            pagination={false}
            size="small"
          />
        </Card>

        {/* Pages */}
        <Card
          title="内容页面"
          extra={
            <Button type="primary" icon={<PlusOutlined />} onClick={handleCreatePage}>
              新建页面
            </Button>
          }
        >
          <Table
            dataSource={pages}
            columns={pageColumns}
            rowKey="id"
            pagination={{ pageSize: 20 }}
            size="small"
          />
        </Card>
      </Space>

      {/* Section Modal */}
      <Modal
        title={editingSection ? '编辑板块' : '新建板块'}
        open={sectionModal}
        onCancel={() => setSectionModal(false)}
        onOk={handleSaveSection}
        width={600}
      >
        <Form form={sectionForm} layout="vertical">
          <Form.Item label="板块名称" name="name" rules={[{ required: true, message: '请输入板块名称' }]}>
            <Input placeholder="如：关于、资源" />
          </Form.Item>
          <Form.Item label="Slug" name="slug" extra="留空自动生成，用于 URL 路径">
            <Input placeholder="about, resources" />
          </Form.Item>
          <Form.Item label="描述" name="description">
            <TextArea rows={3} placeholder="板块描述" />
          </Form.Item>
          <Form.Item label="排序" name="sort_order" initialValue={0}>
            <Input type="number" />
          </Form.Item>
          <Form.Item label="状态" name="is_active" valuePropName="checked" initialValue={true}>
            <Switch checkedChildren="启用" unCheckedChildren="禁用" />
          </Form.Item>
        </Form>
      </Modal>

      {/* Page Modal */}
      <Modal
        title={editingPage ? '编辑页面' : '新建页面'}
        open={pageModal}
        onCancel={() => setPageModal(false)}
        onOk={handleSavePage}
        width={900}
      >
        <Form form={pageForm} layout="vertical">
          <Form.Item label="所属板块" name="section_id" rules={[{ required: true, message: '请选择板块' }]}>
            <Select placeholder="选择板块">
              {sections.map((s) => (
                <Select.Option key={s.id} value={s.id}>
                  {s.name}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item label="页面标题" name="title" rules={[{ required: true, message: '请输入标题' }]}>
            <Input placeholder="如：关于我们、隐私政策" />
          </Form.Item>
          <Form.Item label="Slug" name="slug" extra="留空自动生成，用于 URL 路径">
            <Input placeholder="about-us, privacy-policy" />
          </Form.Item>
          <Form.Item label="页面类型" name="is_external" valuePropName="checked" initialValue={false}>
            <Switch checkedChildren="外链" unCheckedChildren="内容页" />
          </Form.Item>
          <Form.Item
            noStyle
            shouldUpdate={(prev, curr) => prev.is_external !== curr.is_external}
          >
            {({ getFieldValue }) =>
              getFieldValue('is_external') ? (
                <Form.Item label="外链地址" name="external_url" rules={[{ required: true, message: '请输入外链地址' }]}>
                  <Input placeholder="https://example.com" />
                </Form.Item>
              ) : (
                <Form.Item label="页面内容（支持 Markdown / HTML）">
                  <Input.TextArea
                    value={pageContent}
                    onChange={(e) => setPageContent(e.target.value)}
                    rows={12}
                    style={{ fontFamily: 'monospace', fontSize: 13 }}
                    placeholder="支持 Markdown 格式：**粗体** *斜体* # 标题 [链接](url)"
                  />
                </Form.Item>
              )
            }
          </Form.Item>
          <Form.Item label="排序" name="sort_order" initialValue={0}>
            <Input type="number" />
          </Form.Item>
          <Form.Item label="状态" name="is_active" valuePropName="checked" initialValue={true}>
            <Switch checkedChildren="启用" unCheckedChildren="禁用" />
          </Form.Item>
        </Form>
      </Modal>
    </Spin>
  );
};

export default ContentPagesTab;
